import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';

import { LanguageContext } from './language-context';
import useLanguage from './language-hook';
import { useStoreSettingsContext } from './store-settings-context';
import { useStoreDetailsContext } from './store-details-context';
import translate from '../util/translate';
import LoadingSpinner from '../components/UIElements/LoadingSpinner';

/**
 * Binds language state to the current route (must render inside React Router).
 * On store routes, waits for store settings before rendering children to avoid RTL/LTR flicker.
 */
const LanguageProvider = ({
    storeSettings,
    isStoreSettingsReady,
    settingsStoreUsername,
    isPaymentDue,
    token,
    role,
    children,
}) => {
    const { pathname } = useLocation();
    const { fetchStoreSettings } = useStoreSettingsContext();
    const { storeUN } = useStoreDetailsContext();

    const languageState = useLanguage({
        storeSettings,
        pathname,
        isStoreSettingsReady,
        settingsStoreUsername,
    });

    const { currentLanguage, isStoreRoute, isStoreLocaleReady, routeStoreUsername } = languageState;

    const targetStoreUsername = useMemo(() => (
        routeStoreUsername || settingsStoreUsername || storeUN || ''
    ), [routeStoreUsername, settingsStoreUsername, storeUN]);

    useEffect(() => {
        if (!isStoreRoute || !targetStoreUsername) {
            return;
        }

        fetchStoreSettings(targetStoreUsername);
    }, [isStoreRoute, targetStoreUsername, fetchStoreSettings]);

    useEffect(() => {
        if (isPaymentDue && token && role === 'store') {
            message.warning({
                content: translate('auth.paymentDue', currentLanguage),
                key: 'payment',
                duration: 0,
            });
            return;
        }

        message.destroy('payment');
    }, [isPaymentDue, token, role, currentLanguage]);

    if (isStoreRoute && !isStoreLocaleReady) {
        return <LoadingSpinner asOverlay />;
    }

    return (
        <LanguageContext.Provider value={languageState}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageProvider;
